import { Button, EmptyState, ToastProvider, TooltipProvider } from '@marrafa-gioale/gymx-ui-web'
import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'

import { useIsDesktop } from '@/lib/hooks'
import { ToastBridge } from '@/lib/toast-bridge'

/** Contesto disponibile a tutte le rotte, per il prefetch nei loader. */
export interface RouterContext {
  queryClient: QueryClient
}

// Come per i mock in `main.tsx`: `import.meta.env.DEV` è sostituito staticamente,
// così i devtools non finiscono nel bundle di produzione.
const Devtools = import.meta.env.DEV
  ? lazy(async () => {
      const [router, query] = await Promise.all([
        import('@tanstack/react-router-devtools'),
        import('@tanstack/react-query-devtools'),
      ])
      return {
        default: () => (
          <>
            {/* I due pulsanti flottanti vanno in angoli diversi e liberi: in basso a
                sinistra coprirebbero il menu account della sidebar, in basso al
                centro la barra di navigazione mobile. */}
            <router.TanStackRouterDevtools position="bottom-right" />
            <query.ReactQueryDevtools buttonPosition="top-right" />
          </>
        ),
      }
    })
  : null

/**
 * Devtools, solo su desktop.
 *
 * I loro pulsanti flottanti stanno negli angoli in basso, che sotto `lg` sono
 * occupati dalla barra di navigazione: coprono una voce e la rendono non toccabile.
 * Su un viewport da telefono i pannelli sono comunque illeggibili, quindi si
 * montano solo dove servono davvero.
 */
function DevtoolsSlot() {
  const isDesktop = useIsDesktop()
  if (!Devtools || !isDesktop) return null
  return (
    <Suspense fallback={null}>
      <Devtools />
    </Suspense>
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
})

function RootLayout() {
  return (
    <TooltipProvider delayDuration={300}>
      {/* La regione dei messaggi effimeri del design system, montata una volta
          sola. `ToastBridge` la rende raggiungibile da `notify()`, che serve ai
          tre punti fuori da React in `main.tsx` — vedi `@/lib/toast`. */}
      <ToastProvider>
        <Outlet />
        <ToastBridge />
      </ToastProvider>

      <DevtoolsSlot />
    </TooltipProvider>
  )
}

function NotFound() {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <EmptyState
        title="Pagina non trovata"
        description="Il link potrebbe essere vecchio o scritto male."
        action={
          <Button variant="primary" asChild>
            <a href="/">Torna alla dashboard</a>
          </Button>
        }
      />
    </div>
  )
}
